<template>
  <!-- Командная шапка страницы — тот же приём, что у парка (CarsCommandBar):
       одна плашка, внутри строка управления, под ней чипсы активных фильтров. -->
  <v-sheet rounded elevation="1" class="sf-cmd px-4 py-3 mb-3">
    <div class="d-flex align-center flex-wrap" style="gap: 8px 12px">
      <!-- Поле дат приходит слотом: календарём владеет страница, шапка только
           отводит ему место в общей строке. -->
      <div class="sf-cmd__lead">
        <slot />
      </div>

      <template v-if="showControls">
        <v-menu :close-on-content-click="false" offset-y left transition="slide-y-transition" eager>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small outlined class="sf-cmd__btn" v-bind="attrs" v-on="on" data-testid="search-free-filters">
              <v-icon left small>mdi-tune-variant</v-icon>{{ $t("crm.toolbar.filters") }}
              <span v-if="chips.length" class="sf-cmd__badge">{{ chips.length }}</span>
            </v-btn>
          </template>
          <!-- Панель та же, что у парка, в другом составе: поиск свободных не принимает
               ни «в аренде сейчас», ни «активные» (он и так отдаёт только активные и
               только незанятые), зато принимает три своих фильтра. -->
          <cars-filter-panel :value="value" :rental-object="rentalObject" :show-availability="false"
            :state-options="stateOptions" :extras="extras" presets-key="search_free_filter_presets"
            @input="$emit('input', $event)" />
        </v-menu>

        <!-- Порядок, а не фильтр. Сортировать можно только по average_price: цена в
             таблице считается движком под конкретные даты уже после выборки. Для порядка
             этого достаточно — ничего не прячется, сдвигаются лишь соседние строки. -->
        <v-select :value="sort" :items="sortItems" :label="$t('board.sort_by')" hide-details dense
          outlined class="sf-cmd__sort" data-testid="search-free-sort"
          @change="$emit('update:sort', $event)" />
      </template>
    </div>

    <div v-if="showControls && chips.length" class="d-flex align-center flex-wrap mt-3" style="gap: 8px">
      <v-chip v-for="chip in chips" :key="chip.key" small close class="sf-cmd__chip"
        @click:close="$emit('input', clearKey(value, chip.key))">
        <span class="sf-cmd__chipk">{{ chip.label }}:</span>&nbsp;{{ chip.text }}
      </v-chip>
      <v-btn small text class="sf-cmd__clear" @click="$emit('reset')">
        <v-icon left small>mdi-filter-off-outline</v-icon>{{ $t("custom.clear_all") }}
      </v-btn>
    </div>
  </v-sheet>
</template>

<script>
import CarsFilterPanel from "@/components/dashboard/cars/CarsFilterPanel.vue";
import { activeFilterChips, withFilterCleared } from "@/components/dashboard/cars/carsFilterChips";
import { SEARCH_FREE_SELECTABLE_STATES, isDefaultStates } from "@/components/dashboard/bookings/searchFreeFilters";

// «В аренде сейчас» и «Активные» эндпоинт не принимает — чипсов по ним не строим,
// иначе чип обещал бы фильтр, которого нет.
export const SEARCH_FREE_CHIP_KEYS = [
  "state", "car_type", "transmission", "fuel", "tag_ids", "min_seats", "is_air", "no_deposit",
];

export const SEARCH_FREE_EXTRAS = ["min_seats", "is_air", "no_deposit"];

export default {
  name: "SearchFreeFilterBar",
  components: { CarsFilterPanel },
  props: {
    value: { type: Object, required: true },
    rentalObject: { type: String, default: "cars" },
    sort: { type: String, default: "asc" },
    // до первого поиска в шапке только даты: фильтровать и сортировать ещё нечего
    showControls: { type: Boolean, default: false },
  },
  computed: {
    stateOptions() {
      return SEARCH_FREE_SELECTABLE_STATES;
    },
    extras() {
      return SEARCH_FREE_EXTRAS;
    },
    sortItems() {
      return [
        { text: this.$t("custom.cheaper_first"), value: "asc" },
        { text: this.$t("custom.expensive_first"), value: "desc" },
      ];
    },
    chips() {
      const all = activeFilterChips(this.value, {
        t: this.$t.bind(this),
        rentalObject: this.rentalObject,
        include: SEARCH_FREE_CHIP_KEYS,
      });
      // Все четыре состояния отмечены — это исходное положение, а не выбор
      // пользователя: чип-«фильтр», который ничего не сужает, только шумит.
      return isDefaultStates(this.value && this.value.state)
        ? all.filter((chip) => chip.key !== "state")
        : all;
    },
  },
  methods: {
    clearKey: withFilterCleared,
  },
};
</script>

<style scoped>
/* Поле дат занимает всю свободную ширину, управление прижимается к правому краю;
   на узком экране всё переносится и каждый элемент встаёт на свою строку. */
.sf-cmd__lead {
  flex: 1 1 320px;
  min-width: 200px;
}
.sf-cmd__btn {
  text-transform: none;
  letter-spacing: 0;
  height: 40px !important;
}
.sf-cmd__sort {
  flex: 0 1 200px;
  max-width: 220px;
}
.sf-cmd__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 7px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  background: var(--v-success-base, #4caf50);
  color: #fff;
}
.sf-cmd__chip {
  font-size: 12px;
}
.sf-cmd__chipk {
  opacity: 0.6;
}
.sf-cmd__clear {
  text-transform: none;
  letter-spacing: 0;
}
/* На телефоне даты занимают всю строку, а кнопка фильтров и порядок делят вторую.
   Раньше порядок уезжал на третью строку: у v-select своя минимальная ширина, и
   вместе с кнопкой он в строку не влезал. min-width: 0 разрешает ему сжаться —
   без этого flex-элемент не может стать уже своего содержимого. */
@media (max-width: 599px) {
  .sf-cmd__lead {
    flex: 1 1 100%;
  }
  .sf-cmd__btn {
    flex: 0 0 auto;
  }
  .sf-cmd__sort {
    flex: 1 1 0;
    min-width: 0;
    max-width: none;
  }
}
</style>
